import { Link, useParams, useSearchParams } from 'react-router';
import { useMessage, useMessages } from '../api/queries.js';
import { Badge, Card, Dropdown, Loading, Mono, Page, Problem, Table, toneForStatus } from '../components/ui.jsx';
import { formatDateTime, formatDuration, formatNumber } from '../utils/format.js';

/**
 * Every message Broker has carried, and the whole story of a single one. The story is not
 * assembled by this page: it is the message's own slice of the event log, in the order the facts
 * were appended.
 */
export function Messages() {
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const messages = useMessages(query);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  if (messages.isLoading) return <Loading what="i messaggi" />;
  if (messages.error) return <Problem error={messages.error} />;

  return (
    <Page eyebrow="OSSERVA" title="Messages" description={`${formatNumber(messages.data!.total)} messaggi corrispondono al filtro.`}>
      <Card>
        <div className="filters">
          <input
            placeholder="Canale"
            defaultValue={params.get('channel') ?? ''}
            onBlur={(event) => setFilter('channel', event.target.value)}
          />
          <input
            placeholder="Produttore"
            defaultValue={params.get('producer') ?? ''}
            onBlur={(event) => setFilter('producer', event.target.value)}
          />
          <Dropdown
            label="Stato"
            value={params.get('status') ?? ''}
            onChange={(status) => setFilter('status', status)}
            options={[
              { value: '', label: 'Tutti gli stati' },
              ...['published', 'delivered', 'processed', 'retried', 'failed', 'dead-lettered'].map((status) => ({ value: status, label: status })),
            ]}
          />
        </div>

        <Table
          head={
            <tr>
              <th>Pubblicato</th>
              <th>Tipo</th>
              <th>Canale</th>
              <th>Produttore</th>
              <th>Stato</th>
              <th className="right">Consegne</th>
              <th className="right">Elaborazione</th>
            </tr>
          }
          empty="Nessun messaggio con questi filtri."
        >
          {messages.data!.messages.map((message) => (
            <tr key={message.id}>
              <td className="muted small">{formatDateTime(message.publishedAt)}</td>
              <td>
                <Link to={`/messages/${message.id}`}>{message.type}</Link>
              </td>
              <td>{message.channel}</td>
              <td>{message.producer}</td>
              <td>
                <Badge tone={toneForStatus(message.status)}>{message.status}</Badge>
              </td>
              <td className="right">
                {formatNumber(message.deliveries)}
                {message.failures > 0 ? <span className="muted small"> ({message.failures} falliti)</span> : null}
              </td>
              <td className="right">{formatDuration(message.processingMs)}</td>
            </tr>
          ))}
        </Table>
      </Card>
    </Page>
  );
}

export function MessageDetail() {
  const { id = '' } = useParams();
  const message = useMessage(id);

  if (message.isLoading) return <Loading what="il messaggio" />;
  if (message.error) return <Problem error={message.error} />;
  const { view, envelope, timeline } = message.data!;

  return (
    <Page
      eyebrow="OSSERVA"
      title={view.type}
      description={`Pubblicato da ${view.producer} su ${view.channel}.`}
      actions={<Badge tone={toneForStatus(view.status)}>{view.status}</Badge>}
    >
      <div className="grid-2">
        <Card title="Busta">
          <dl className="details">
            <dt>Id</dt>
            <dd>
              <Mono value={view.id} />
            </dd>
            <dt>Correlazione</dt>
            <dd>
              <Link to={`/correlations/${encodeURIComponent(view.correlationId)}`}>
                <Mono value={view.correlationId} />
              </Link>
            </dd>
            <dt>Causato da</dt>
            <dd>{view.causationId ? <Link to={`/messages/${view.causationId}`}><Mono value={view.causationId} /></Link> : '—'}</dd>
            <dt>Tipo</dt>
            <dd>
              {envelope.kind} · {envelope.type}
            </dd>
            <dt>Pubblicato</dt>
            <dd>{formatDateTime(envelope.publishedAt)}</dd>
            <dt>Scadenza</dt>
            <dd>{envelope.expiresAt ? formatDateTime(envelope.expiresAt) : 'nessuna'}</dd>
          </dl>
        </Card>

        <Card title="Payload" hint="Esattamente come il produttore l&apos;ha inviato">
          <pre className="payload">{JSON.stringify(envelope.payload, null, 2)}</pre>
        </Card>
      </div>

      <Card title="Cronologia" hint="I fatti registrati su questo messaggio, in ordine">
        <Table
          head={
            <tr>
              <th>#</th>
              <th>Quando</th>
              <th>Fatto</th>
              <th>Consumatore</th>
              <th>Tentativo</th>
              <th>Dettaglio</th>
            </tr>
          }
          empty="Nessun fatto registrato."
        >
          {timeline.map((entry) => (
            <tr key={entry.sequence}>
              <td className="mono muted">{entry.sequence}</td>
              <td className="muted small">{formatDateTime(entry.occurredAt)}</td>
              <td>
                <Badge tone={toneForStatus(entry.type.replace('message.', ''))}>{entry.type}</Badge>
              </td>
              <td>{entry.consumer ?? '—'}</td>
              <td className="right">{entry.attempt ?? '—'}</td>
              <td className="muted small">{entry.detail ?? ''}</td>
            </tr>
          ))}
        </Table>
      </Card>
    </Page>
  );
}
